/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   NewArrivalsPanel.tsx
 *     Feature         :   254-pipeline-monitoring-overhaul (US2)
 *     Description     :   Surfaces newly-arrived / reappeared pipelines awaiting triage
 *                         (FR-013/014/017) so a new pipeline never blends silently into the
 *                         fleet. Admins triage inline — adopt at a tier or retire. Explicit
 *                         "no new arrivals" state (never an ambiguous empty box). Kit + tokens.
 *     Constitution    :   Principle XLV, XXXVII
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-24
 * =========================================================================================
 */
import { Card, Badge, Button } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useNewArrivals, useCurationMutations } from '@/hooks/usePipelineMonitoring';
import { formatEastern } from '@/utils/dateFormatter';
import { roleSatisfies } from '../../../../../shared/access/roles';

export function NewArrivalsPanel(): JSX.Element | null {
  const { user } = useAuth();
  const isAdmin = roleSatisfies(user?.role, ['Admin']);
  const { data } = useNewArrivals();
  const { patch } = useCurationMutations();
  const arrivals = data?.arrivals ?? [];

  // Hide entirely when there are none AND the query has loaded — no ambiguous empty box.
  if (data && arrivals.length === 0) return null;

  return (
    <Card title="New arrivals">
      <p className="mb-2 text-xs text-[var(--color-text-secondary)]">
        Pipelines reporting for the first time (or a retired one reporting again). Adopt them at an importance tier or retire them.
      </p>
      <ul className="divide-y divide-[var(--color-border)]">
        {arrivals.map((a) => (
          <li key={a.appInstance} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">{a.appName}</span>
                <Badge variant={a.lifecycleState === 'reappeared' ? 'danger' : 'warning'}>{a.lifecycleState}</Badge>
              </div>
              <div className="text-xs text-[var(--color-text-secondary)]">
                first seen {a.firstSeenAt ? formatEastern(a.firstSeenAt) : 'unknown'} · {a.runCount} run(s)
              </div>
            </div>
            {isAdmin && (
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="primary" disabled={patch.isPending}
                  onClick={() => patch.mutate({ appInstance: a.appInstance, patch: { lifecycleState: 'active', importanceTier: 'Standard' } })}>Adopt</Button>
                <Button size="sm" variant="outline" disabled={patch.isPending}
                  onClick={() => patch.mutate({ appInstance: a.appInstance, patch: { lifecycleState: 'retired' } })}>Retire</Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default NewArrivalsPanel;
